// @ts-nocheck
/* eslint-disable react/prop-types */
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { alpha } from '@mui/material';

import { ReactComponent as KeyboardArrowDownIcon } from '../assets/KeyboardArrowDownIcon.svg';
import { ReactComponent as KeyboardArrowUpIcon } from '../assets/KeyboardArrowUpIcon.svg';

import SolyticsBox from './SolyticsBox';
import SolyticsDropdownPanel from './SolyticsDropdownPanel';
import SolyticsInput from './SolyticsInput';
import SolyticsChip from './SolyticsChip';
import SolyticsPopper from './SolyticsPopper';
import SolyticsIconButton from './SolyticsIconButton';
import SolyticsButton from './SolyticsButton';

// KEY DESIGN TOKENS
const POPUP_BORDER_RADIUS = 0.5;
const SMALL_INPUT_PADDING = '6px 12px';
const MEDIUM_INPUT_PADDING = '8px 12px';

const DEFAULT_NO_OPTIONS_TEXT = 'No options available.';
const DEFAULT_SIZE = 'medium';
const DEFAULT_PLACEHOLDER = 'Select...';
/**
 * SolyticsDropdownInput - A custom dropdown select component with panel-based options
 *
 * @component
 * @description
 * SolyticsDropdownInput renders a read-only SolyticsInput with a dropdown panel
 * (SolyticsDropdownPanel) anchored via SolyticsPopper. Supports single and multi-select,
 * tabs, filters, searchable options, and customizable visual presets via styleVariant.
 *
 * @param {string} [variant='basic'] - Panel variant: 'basic' | 'multi' | 'search' | 'tabs-single' | 'tabs-multi' | 'filter-dropdown' | 'icon'
 * @param {string} [type='input'] - Trigger type: 'input' (text field, default) | 'box' (compact outlined button)
 * @param {boolean} [fullWidth=true] - If true, takes full container width
 * @param {Array<{label: string, value: *}>} [options=[]] - Options for the dropdown
 * @param {Array} [filters=[]] - Filter options for filter-dropdown variant
 * @param {string} [filterKey] - Key used for filtering
 * @param {Object} [tabs={}] - Tab-grouped options: { tabName: [...options] }
 * @param {*} value - Selected value (single) or array of values (multiple)
 * @param {function} onChange - Change handler; receives { target: { value } }
 * @param {boolean} [searchable=false] - If true, enables search within the panel
 * @param {boolean} [multiple=false] - If true, allows multi-select with chips
 * @param {boolean} [withCheckbox=false] - If true, shows checkboxes in multi-select
 * @param {boolean} [showIcons=false] - If true, shows icons next to options
 * @param {string} label - Label text rendered via SolyticsLabel
 * @param {string} [placeholder='Select...'] - Placeholder when no value selected
 * @param {string} id - HTML id attribute
 * @param {string} name - Name attribute (required)
 * @param {boolean} error - If true, shows error state
 * @param {string} helperText - Helper text below the dropdown
 * @param {boolean} disabled - If true, disables the dropdown
 * @param {boolean} required - If true, marks field as required
 * @param {Object} [infoIcon] - Info icon config: { tooltip, placement?, icon? }
 * @param {string} [noOptionsText='No options available.'] - Text shown when no options exist
 * @param {string} [styleVariant] - Visual preset (e.g., 'calendar-month', 'calendar-year')
 * @param {string} [size='medium'] - Input size: 'small' | 'medium'
 * @param {function} [customPanelRenderer] - Optional render function for custom popper content
 * @param {string|number} [panelWidth] - Optional custom width for the popper panel
 * @param {boolean} [showSelectionCount=true] - For box type, whether to show count of selected items
 * @param {function} [onBlur] - Called when the dropdown loses focus / closes.
 *   - Single select: deferred to next microtask after selection so React commits
 *     the value before any blur-triggered validation runs.
 *   - Multi-select: fires when the panel closes (click-away or Escape), NOT per-selection.
 *   - Box type: fires on click-away only.
 *
 * @returns {React.ReactElement|null} A dropdown component, or null if name/onChange missing
 */
const SolyticsDropdownInput = ({
  variant = 'basic',
  type = 'input',
  fullWidth = true,
  options = [],
  filters = [],
  filterKey,
  tabs = {},
  value,
  onChange,
  searchable = false,
  multiple = false,
  withCheckbox = false,
  showIcons = false,
  label,
  placeholder = DEFAULT_PLACEHOLDER,
  id,
  name,
  size = DEFAULT_SIZE,
  error,
  helperText,
  disabled,
  required,
  infoIcon,
  noOptionsText = DEFAULT_NO_OPTIONS_TEXT,
  styleVariant, // For any specific styling needs in dropdown panel (e.g. calendar month/year selectors)
  //for box types, we allow a custom panel renderer to enable use cases like the month selector with icons
  customPanelRenderer,
  panelWidth,
  startIcon,
  color = 'primary',
  minWidth,
  showSelectionCount = true,
  onBlur,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const inputRef = useRef(null);
  const anchorNodeRef = useRef(null);

  // Stable ref callback: without this, an inline arrow fn identity changes on
  // every render, so React re-invokes it (setAnchorEl) each time — combined
  // with a parent re-render loop this trips "Maximum update depth exceeded".
  const setAnchorElRef = useCallback((el) => {
    if (el && anchorNodeRef.current !== el) {
      anchorNodeRef.current = el;
      setAnchorEl(el);
    }
  }, []);

  const isBox = useMemo(() => type === 'box', [type]);

  const open = Boolean(isOpen);
  const isMulti = multiple;
  const selectedValues = isMulti ? value || [] : value ? [value] : [];
  const allOptions = variant?.includes('tabs')
    ? Object.values(tabs || {})?.flat()
    : options || [];

  /**
   * Close the panel and fire onBlur.
   *
   * onBlur is always called synchronously here — used for multi-select panel close
   * and arrow-button close, where the value has already been committed.
   */
  const closeAndBlur = () => {
    setIsOpen(false);
    onBlur?.();
  };

  const handleSelect = (val) => {
    if (multiple && Array.isArray(val)) {
      onChange?.({ target: { value: val } });
      // onBlur fires on panel close, not per-selection
      return;
    }

    if (multiple) {
      const updated = selectedValues?.includes(val)
        ? selectedValues?.filter((v) => v !== val)
        : [...(selectedValues || []), val];
      onChange?.({ target: { value: updated } });
      // onBlur fires on panel close, not per-selection
      return;
    }

    // Single select: close panel first, then defer onBlur to the next microtask
    // so React commits the new value before form validation runs.
    onChange?.({ target: { value: val } });
    setIsOpen(false);
    queueMicrotask(() => onBlur?.());
  };

  useEffect(() => {
    if (!inputRef?.current) return;
    if (isBox) {
      setAnchorEl(inputRef.current);
    }
  }, [isBox]);

  // Create startAdornment for multi-select chips
  const renderStartAdornment = () => {
    if (!isMulti || !selectedValues?.length) return null;

    const firstLabel = allOptions?.find(
      (o) => o.value === selectedValues?.[0]
    )?.label;
    const remainingCount = selectedValues.length - 1;

    return (
      <SolyticsBox display="flex" alignItems="center" gap={0.5}>
        {firstLabel && (
          <SolyticsChip
            shape="pill"
            variant="filled"
            color="primary"
            label={firstLabel}
            title={firstLabel}
            size="medium"
          />
        )}
        {remainingCount > 0 && (
          <SolyticsChip
            shape="pill"
            variant="text"
            color="primary"
            label={`+${remainingCount} more`}
            title={allOptions
              ?.slice(1)
              ?.map((o) => o.label)
              ?.join(', ')}
            size="medium"
          />
        )}
      </SolyticsBox>
    );
  };

  const displayValue = !isMulti
    ? allOptions?.find((o) => o?.value === value)?.label || ''
    : '';

  const rootSx = useMemo(() => {
    if (!isBox) {
      return {
        cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative',
        '& .input-base-anchor': {
          position: 'absolute',
          left: 0,
          right: 0,
          width: '100%',
          pointerEvents: 'none',
          visibility: 'hidden',
          top: label ? '20px' : 0,
          height: '40px',
          zIndex: -1,
        },
        '& .dropdown-icons-wrapper': {
          position: 'absolute',
          right: 10,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          zIndex: 1,
          pointerEvents: 'none',
          '& > *': {
            pointerEvents: 'auto',
          },
          top: () => {
            const inputBaseHalfHeight = size === 'small' ? '16px' : '20px';
            if (label) {
              // Label area = 16px lineHeight + 4px marginBottom = 20px
              return `calc(20px + ${inputBaseHalfHeight})`;
            }
            return inputBaseHalfHeight;
          },
          transform: 'translateY(-50%)',
          '& .MuiIconButton-root': {
            width: '24px',
            height: '24px',
            padding: '4px',
            boxSizing: 'border-box',
          },
        },
        '& .MuiInputBase-input': {
          padding: `${
            size === 'small' ? SMALL_INPUT_PADDING : MEDIUM_INPUT_PADDING
          } !important`,
          paddingRight: '42px !important',
          minWidth: '30px',
          flex: '1 1 auto',
        },
      };
    }

    const hasSelection = selectedValues.length > 0;
    return {
      display: 'inline-flex',
      alignItems: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer',
      '& .MuiButton-root': {
        ...(hasSelection && {
          backgroundColor: (theme) => theme.palette.selection.main,
          borderColor: (theme) => `${theme.palette.primary.main} !important`,
          color: (theme) => theme.palette.primary.main,
          '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
            color: (theme) => theme.palette.primary.main,
          },
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            borderColor: (theme) => `${theme.palette.primary.main} !important`,
            '& .MuiButton-startIcon, & .MuiButton-endIcon, & svg': {
              color: (theme) => theme.palette.primary.main,
            },
          },
        }),
      },
    };
  }, [isBox, disabled, label, size, selectedValues.length]);

  const popoverId = `${id || name}-popover`;
  const selectedLabel = allOptions?.find((o) => o?.value === value)?.label;
  if (!name || !onChange) return null;

  return (
    <>
      <SolyticsBox
        ref={inputRef}
        onClick={
          isBox
            ? undefined
            : () => {
                if (!disabled) {
                  setIsOpen((prev) => !prev);
                }
              }
        }
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? popoverId : undefined}
        sx={rootSx}
      >
        {isBox ? (
          // ── Box button trigger ────────────────────────────────────────────
          <SolyticsButton
            variant="outlined"
            color={color}
            size={size}
            disabled={disabled}
            aria-label={label || placeholder}
            minWidth={minWidth}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) setIsOpen((prev) => !prev);
            }}
            {...(startIcon ? { startIcon } : {})}
            endIcon={
              open ? (
                <KeyboardArrowUpIcon width={20} height={20} />
              ) : (
                <KeyboardArrowDownIcon width={20} height={20} />
              )
            }
          >
            <SolyticsBox
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '120px',
              }}
            >
              {selectedLabel || label || placeholder}
            </SolyticsBox>
            {showSelectionCount && selectedValues.length > 0 && (
              <SolyticsBox
                component="span"
                aria-label={`${selectedValues.length} selected`}
                sx={{
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: (theme) => theme.palette.primary.contrastText,
                  minWidth: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ml: 0.75,
                  px: '3px',
                  fontSize: '11px',
                  fontWeight: 600,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                {selectedValues.length}
              </SolyticsBox>
            )}
          </SolyticsButton>
        ) : (
          // ── Standard input trigger ────────────────────────────────────────
          <>
            <SolyticsBox
              ref={setAnchorElRef}
              className="input-base-anchor"
              aria-hidden="true"
            />
            <SolyticsInput
              label={label}
              {...(id && { id })}
              {...(name && { name })}
              {...(placeholder && selectedValues?.length === 0
                ? { placeholder }
                : {})}
              {...(error && { error })}
              {...(helperText && { helperText })}
              {...(disabled && { disabled })}
              {...(required && { required })}
              {...(styleVariant && { styleVariant })}
              fullWidth={fullWidth}
              variant="select"
              size={size}
              value={displayValue}
              readOnly
              {...(disabled && { disabled })}
              onBlur={onBlur}
              onChange={() => {}}
              infoIcon={infoIcon}
              startAdornment={renderStartAdornment()}
              slotProps={{
                root: {
                  onClick: (e) => {
                    e.stopPropagation();
                    if (!disabled) {
                      setIsOpen((prev) => !prev);
                    }
                  },
                },
              }}
            />
            <SolyticsBox
              className="dropdown-icons-wrapper"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SolyticsIconButton
                aria-label={open ? 'Close' : 'Open'}
                size="small"
                disabled={disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  if (disabled) return;
                  if (open) {
                    closeAndBlur();
                  } else {
                    setIsOpen(true);
                  }
                }}
              >
                <KeyboardArrowDownIcon
                  width={20}
                  height={20}
                  style={{
                    color: 'inherit',
                    transform: open ? 'scaleY(-1)' : 'scaleY(1)',
                  }}
                />
              </SolyticsIconButton>
            </SolyticsBox>
          </>
        )}
      </SolyticsBox>
      <SolyticsPopper
        transition
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
          {
            name: 'matchWidth',
            enabled: true,
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn: ({ state }) => {
              if (!panelWidth) {
                // eslint-disable-next-line no-param-reassign
                state.styles.popper.width = `${state.rects.reference.width}px`;
              }
            },
          },
        ]}
        enableClickAway
        onClickAway={() => {
          if (isOpen) closeAndBlur();
        }}
      >
        <SolyticsBox
          role={customPanelRenderer ? undefined : 'listbox'}
          sx={{
            minWidth: 'fit-content',
            width: panelWidth ?? '100%',
            borderRadius: POPUP_BORDER_RADIUS,
            boxShadow: '0px 2px 4px -1px #0000000F, 0px 4px 6px -1px #0000001A',
            background: (theme) => theme.palette.background.default,
            border: (theme) => `1px solid ${theme.palette.grey[60]}`,
          }}
        >
          {customPanelRenderer ? (
            customPanelRenderer({
              open,
              selectedValues,
              options: allOptions,
              onSelect: handleSelect,
              closePopper: closeAndBlur,
            })
          ) : (
            <SolyticsDropdownPanel
              {...(id && { id })}
              {...(name && { name })}
              {...(variant && { variant })}
              {...(filterKey && { filterKey })}
              {...(searchable && { searchable })}
              {...(showIcons && { showIcons })}
              {...(multiple && { multiple })}
              {...(withCheckbox && { withCheckbox })}
              {...(tabs && Object.keys(tabs)?.length && { tabs })}
              {...(options && options?.length && { options })}
              {...(filters && filters?.length && { filters })}
              selectedValues={selectedValues}
              onSelect={handleSelect}
              noOptionsText={noOptionsText}
            />
          )}
        </SolyticsBox>
      </SolyticsPopper>
    </>
  );
};

export default SolyticsDropdownInput;

/**
 * ## onBlur / Validation Behaviour
 *
 * | Scenario                        | When onBlur fires                                        |
 * |---------------------------------|----------------------------------------------------------|
 * | Single select, item picked      | Next microtask after selection (value commits first)     |
 * | Multi-select, panel closed      | On click-away or arrow-button close                      |
 * | Arrow button clicked to close   | Immediately (value already committed)                    |
 * | Box type, click-away            | On click-away                                            |
 *
 * For react-hook-form / Formik with multi-select, register the field with
 * `{ shouldUnregister: false }` and validate `onBlur` (not `onChange`) so
 * validation runs once when the panel closes rather than on every selection.
 *
 * ## Features
 * - Read-only SolyticsInput as trigger with dropdown arrow icon
 * - SolyticsDropdownPanel for option rendering with search, tabs, filters, and checkboxes
 * - Single select (closes on selection) and multi-select (chips display)
 * - Multi-select shows first chip + "+N more" chip indicator
 * - Optional clearable button on hover
 * - StyleVariant support for borderless/compact visual presets (calendar, color picker)
 * - Popper-based positioning with click-away close
 * - WCAG 2.1 AA compliant with combobox role and aria attributes
 *
 * ## Examples
 *
 * ```jsx
 * // Basic single select
 * <SolyticsDropdownInput
 *   name="status"
 *   label="Status"
 *   options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
 *   value={status}
 *   onChange={(e) => setStatus(e.target.value)}
 * />
 *
 * // Multi-select with search
 * <SolyticsDropdownInput
 *   name="categories"
 *   label="Categories"
 *   multiple
 *   searchable
 *   withCheckbox
 *   options={categoryOptions}
 *   value={selectedCategories}
 *   onChange={(e) => setSelectedCategories(e.target.value)}
 * />
 *
 * // Calendar month selector (borderless)
 * <SolyticsDropdownInput
 *   name="month"
 *   options={monthOptions}
 *   value={month}
 *   onChange={handleMonthChange}
 *   styleVariant="calendar-month"
 * />
 * ```
 */
